export type BadgeTone = "success" | "warning" | "danger" | "info" | "neutral";

const DANGER_STATUSES = new Set(["cancelled", "canceled", "discontinued", "overdue", "rejected", "failed"]);
const SUCCESS_STATUSES = new Set(["active", "paid", "completed", "delivered", "received", "approved"]);
const WARNING_STATUSES = new Set(["draft", "pending", "partial", "refunded"]);
const INFO_STATUSES = new Set(["sent", "placed", "confirmed", "shipped", "processing"]);

// Maps the many entity-specific status vocabularies (product/order/invoice/
// sale/payment/... each define their own enum) onto one shared badge tone by
// meaning, so a screen only needs to pass its raw status string.
export const statusTone = (status: string): BadgeTone => {
  const s = status?.toLowerCase();
  if (DANGER_STATUSES.has(s)) return "danger";
  if (SUCCESS_STATUSES.has(s)) return "success";
  if (WARNING_STATUSES.has(s)) return "warning";
  if (INFO_STATUSES.has(s)) return "info";
  return "neutral";
};

// OrganizationSubscription.status ("pending"/"active"/"expired"/"cancelled"/
// "suspended") — the generic `statusTone` above doesn't distinguish
// "expired"/"suspended" from an unmapped default, which is why the platform
// owner's Organizations/Subscriptions screens and the org owner's own
// my-subscription.tsx used to each carry their own slightly different local
// mapping. One shared helper keeps every subscription-status badge across
// those screens consistent.
const SUBSCRIPTION_STATUS_TONE: Record<string, BadgeTone> = {
  active: "success",
  pending: "warning",
  suspended: "warning",
  expired: "danger",
  cancelled: "danger",
};

export const subscriptionStatusTone = (status?: string | null): BadgeTone =>
  SUBSCRIPTION_STATUS_TONE[(status ?? "").toLowerCase()] ?? "neutral";
